import { useState, useEffect } from 'react'
import { Routes, Route, useLocation, Link } from 'react-router-dom'
import { trackPageView, setAdminOptOut } from './utils/analytics'
import { useAuth } from './hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { ThemeProvider } from './components/theme-provider'
import { Toaster } from './components/ui/toaster'
import Navbar from './components/navbar'
import BottomNav from './components/bottom-nav'
import HomePage from './pages/home'
import DashboardPage from './pages/dashboard'
import ClimbsPage from './pages/climbs'
import NotFoundPage from './pages/not-found'
import SignInPage from './pages/sign-in'
import SignUpPage from './pages/sign-up'
import InviteUserPage from './pages/invite-user'
import AcceptInvitationPage from './pages/accept-invitation'
import GuardianAcceptPage from './pages/guardian-accept'
import GuardianSignUpPage from './pages/guardian-sign-up'
import MyAthletesPage from './pages/my-athletes'
import ForgotPasswordPage from './pages/forgot-password'
import ResetPasswordPage from './pages/reset-password'
import ProfilePage from './pages/profile'
import NotificationSettingsPage from './pages/notification-settings'
import AiIntegrationPage from './pages/ai-integration'
import UserProfile from './pages/user-profile'
import GymProfile from './pages/gym-profile'
import DeviceAuthPage from './pages/device-auth'
import GymLeaderboard from './pages/gym-leaderboard'
import CragProfile from './pages/crag-profile'
import MaintainerProfile from './pages/maintainer-profile'
import GymSettings from './pages/gym-settings'
import QrPosterPage from './pages/qr-poster'
import CragSheetPage from './pages/crag-sheet'
import CompetitionInstructionsPage from './pages/competition-instructions'
import CompetitionResultsPrintPage from './pages/competition-results-print'
import GymScansPage from './pages/gym-scans'
import GymScansListPage from './pages/gym-scans-list'
import CompetitionProfile from './pages/competition-profile'
import TrophyProfile from './pages/trophy-profile'
import CompetitionRoundDetail from './pages/competition-round-detail'
import CompetitionCategoryStandings from './pages/competition-category-standings'
import CompetitionSettings from './pages/competition-settings'
import JudgeBlock from './pages/judge-block'
import TeamManagerAthlete from './pages/team-manager-athlete'
import RoundHelpInvitation from './pages/round-help-invitation'
import RoundHelperGate from './components/competition/round-delegation/RoundHelperGate'
import SearchPage from './pages/search'
import ClimbProfile from './pages/climb-profile'
import ShareClimbAdvancedPage from './pages/share-climb-advanced'
import ClimbEditorPage from './pages/climb-editor'
import ContactPage from './pages/contact'
import LegalPage from './pages/legal'
import { ConsentBanner, ConsentPreferencesLink } from './components/consent/ConsentBanner'
import NotificationsPage from './pages/notifications'
import MyStats from './pages/my-stats'
import TrainingDiary from './pages/training-diary'
import TrainingPlans from './pages/training-plans'
import TrainingPlanForm from './pages/training-plan-form'
import TrainingPlanBuilder from './pages/training-plan-builder'
import CoachInvitePage from './pages/coach-invite'
import MyExercises from './pages/my-exercises'
import CustomExerciseForm from './pages/custom-exercise-form'
import WorkoutDetail from './pages/workout-detail'
import WorkoutResultDetail from './pages/workout-result-detail'
import { AuthProvider } from './contexts/auth-context'
import { BreadcrumbProvider } from './contexts/breadcrumb-context'
import { BottomNavProvider } from './contexts/bottom-nav-context'
import { AppFooterProvider, useAppFooter } from './contexts/app-footer-context'
import { NotificationProvider } from './contexts/notification-context'
import ProtectedRoute from './components/protected-route'
import { TooltipProvider } from './components/ui/tooltip'
import PWAUpdatePrompt from './components/PWAUpdatePrompt'
import ServiceWorkerStatus from './components/ServiceWorkerStatus'
import { TimerMiniPlayer } from './components/workout-timer/TimerMiniPlayer'
import { WorkoutTimerDialog } from './components/workout-timer/WorkoutTimerDialog'
import { EndWorkoutModal } from './components/workout-timer/EndWorkoutModal'

function AnalyticsGuard() {
  const { user } = useAuth()
  useEffect(() => {
    // Driven by who is signed in right now, not latched on the first admin we
    // see: an admin signing out used to leave the session opted out for good.
    setAdminOptOut(user?.role === 'admin')
  }, [user])
  return null
}

/**
 * Under every page but the ones that ask to go without it (`useHideAppFooter`):
 * a climb and its editor, where the picture is the page and the last field
 * should be the last thing on it.
 */
function AppFooter() {
  const { t } = useTranslation()
  const { hidden } = useAppFooter()
  if (hidden) return null

  return (
    <footer className="py-6 text-center text-sm text-muted-foreground">
      <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
      {/* The way back to a choice already made. Keeping it in the footer of
          (nearly) every page is what makes withdrawing consent as easy as
          giving it, which is the part the law is specific about. */}
      <p className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs">
        <Link to="/privacy-policy" className="hover:text-foreground hover:underline">
          {t('consent.links.privacyPolicy')}
        </Link>
        <Link to="/cookie-policy" className="hover:text-foreground hover:underline">
          {t('consent.links.cookiePolicy')}
        </Link>
        <ConsentPreferencesLink className="hover:text-foreground hover:underline" />
        <Link to="/credits" className="hover:text-foreground hover:underline">
          {t('footer.credits')}
        </Link>
      </p>
    </footer>
  )
}

function App() {
  const { t } = useTranslation()
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const location = useLocation()

  useEffect(() => {
    trackPageView(location.pathname + location.search)
  }, [location])

  useEffect(() => {
    function handleOnlineStatusChange() {
      setIsOnline(navigator.onLine)
    }

    window.addEventListener('online', handleOnlineStatusChange)
    window.addEventListener('offline', handleOnlineStatusChange)

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange)
      window.removeEventListener('offline', handleOnlineStatusChange)
    }
  }, [])

  return (
    <ThemeProvider defaultTheme="dark" storageKey="divepeaks-theme">
      <TooltipProvider>
      <AuthProvider>
        <NotificationProvider>
        <BreadcrumbProvider>
        <BottomNavProvider>
        <AppFooterProvider>
        {/* Clearance for the floating BottomNav pill (mobile only) goes below the
            footer, not between the content and the footer. */}
        <div className="min-h-screen flex flex-col overflow-x-clip pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0">
          {!isOnline && (
            <div className="bg-destructive text-destructive-foreground text-center py-2 text-sm">
              {t('offline.message')}
            </div>
          )}
          <Navbar />
          <TimerMiniPlayer />
          {/* The plan builder is the one full-bleed page: its weeks × exercises
              grid uses every pixel of the window instead of the centred column. */}
          <main
            className={`flex-grow px-4 sm:px-6 py-3 ${
              /^\/training-plans\/(?!new$)[^/]+$/.test(location.pathname) ? 'w-full' : 'container'
            }`}
          >
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/climbs" element={
                <ProtectedRoute>
                  <ClimbsPage />
                </ProtectedRoute>
              } />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/sign-up" element={<SignUpPage />} />
              {/* The same step for a parent entering a child: the athletes
                  first, then the account that will hold them. */}
              <Route path="/sign-up/guardian" element={<GuardianSignUpPage />} />
              <Route path="/invitation/accept" element={<AcceptInvitationPage />} />
              <Route path="/guardian/accept" element={<GuardianAcceptPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invite" element={
                <ProtectedRoute>
                  <InviteUserPage />
                </ProtectedRoute>
              } />
              <Route path="/my-stats" element={
                <ProtectedRoute>
                  <MyStats />
                </ProtectedRoute>
              } />
              <Route path="/training-diary" element={
                <ProtectedRoute>
                  <TrainingDiary />
                </ProtectedRoute>
              } />
              {/* The user's own private exercises: list, create, edit. */}
              <Route path="/training-diary/exercises" element={
                <ProtectedRoute>
                  <MyExercises />
                </ProtectedRoute>
              } />
              <Route path="/training-diary/exercises/new" element={
                <ProtectedRoute>
                  <CustomExerciseForm />
                </ProtectedRoute>
              } />
              <Route path="/training-diary/exercises/:id/edit" element={
                <ProtectedRoute>
                  <CustomExerciseForm />
                </ProtectedRoute>
              } />
              <Route path="/training-diary/workouts/:workoutId" element={
                <ProtectedRoute>
                  <WorkoutDetail />
                </ProtectedRoute>
              } />
              <Route path="/training-diary/workout-results/:resultId" element={
                <ProtectedRoute>
                  <WorkoutResultDetail />
                </ProtectedRoute>
              } />
              <Route path="/training-diary/workouts/:workoutId/results/new" element={
                <ProtectedRoute>
                  <WorkoutResultDetail />
                </ProtectedRoute>
              } />
              <Route path="/training-plans" element={
                <ProtectedRoute>
                  <TrainingPlans />
                </ProtectedRoute>
              } />
              <Route path="/training-plans/new" element={
                <ProtectedRoute>
                  <TrainingPlanForm />
                </ProtectedRoute>
              } />
              <Route path="/training-plans/:id" element={
                <ProtectedRoute>
                  <TrainingPlanBuilder />
                </ProtectedRoute>
              } />
              <Route path="/training-plans/:id/details" element={
                <ProtectedRoute>
                  <TrainingPlanForm />
                </ProtectedRoute>
              } />
              <Route path="/coach/:token" element={<CoachInvitePage />} />
              <Route path="/users/:slug" element={<UserProfile />} />
              <Route path="/gyms/:slug" element={<GymProfile />} />
              <Route path="/gyms/:slug/leaderboard" element={<GymLeaderboard />} />
              {/* The printable wall poster — staff only, guarded in the page itself. */}
              <Route path="/gyms/:slug/qr-poster" element={
                <ProtectedRoute>
                  <QrPosterPage kind="gym" />
                </ProtectedRoute>
              } />
              {/* Public: opened by whoever scanned a device's OTP, account or not. */}
              <Route path="/gyms/:gymSlug/devices/:code/auth/:otp" element={<DeviceAuthPage />} />
              <Route path="/gyms/:slug/settings" element={
                <ProtectedRoute>
                  <GymSettings />
                </ProtectedRoute>
              } />
              <Route path="/gyms/:gymSlug/scans" element={
                <ProtectedRoute>
                  <GymScansListPage />
                </ProtectedRoute>
              } />
              <Route path="/gyms/:gymSlug/scans/:scanId" element={
                <ProtectedRoute>
                  <GymScansPage />
                </ProtectedRoute>
              } />
              {/* A circuit's own page. Its stages are competitions and keep
                  their /competitions/... URLs — a stage *is* one. */}
              <Route path="/trophies/:slug" element={<TrophyProfile />} />
              <Route path="/competitions/:slug" element={<CompetitionProfile />} />
              {/* The entry instructions as two PDFs, made in the browser. For
                  the competition's admins and desk only: the page itself
                  checks the role, this asks for a session first. */}
              <Route path="/competitions/:slug/instructions.pdf" element={
                <ProtectedRoute>
                  <CompetitionInstructionsPage />
                </ProtectedRoute>
              } />
              {/* A stage's results as a sheet to print — public, like them. */}
              <Route path="/competitions/:slug/results/print" element={<CompetitionResultsPrintPage />} />
              <Route path="/competitions/:slug/qr-poster" element={
                <ProtectedRoute>
                  <QrPosterPage kind="competition" />
                </ProtectedRoute>
              } />
              <Route path="/competitions/:slug/rounds/:roundId" element={<CompetitionRoundDetail />} />
              {/* A helper's phone, scoring a round for a team manager or an
                  athlete with no account of its own: the link it was sent
                  (the invitation), then the same pages a team manager and an
                  athlete use, under the link and never behind a sign-in —
                  RoundHelperGate turns the phone's delegation on for them. */}
              <Route path="/competitions/:slug/rounds/:roundId/aiuto/:token">
                <Route index element={<RoundHelpInvitation />} />
                <Route element={<RoundHelperGate />}>
                  <Route path="athletes/:participantId?" element={<TeamManagerAthlete />} />
                  <Route path="scheda" element={<CompetitionRoundDetail />} />
                </Route>
              </Route>
              {/* A class's standings across its turns: one table, open to the
                  room once every turn is closed. */}
              <Route path="/competitions/:slug/standings/:categoryId" element={<CompetitionCategoryStandings />} />
              {/* One block on the wall, as the judge standing at it scores
                  it: one page for both disciplines, under the two addresses
                  the round's type gives it. */}
              <Route path="/competitions/:slug/rounds/:roundId/climbs/:roundClimbId/judge-lead" element={
                <ProtectedRoute>
                  <JudgeBlock />
                </ProtectedRoute>
              } />
              <Route path="/competitions/:slug/rounds/:roundId/climbs/:roundClimbId/judge-boulder" element={
                <ProtectedRoute>
                  <JudgeBlock />
                </ProtectedRoute>
              } />
              {/* One athlete's whole round, as their club's team manager — or
                  a parent — works down it. The transpose of the judge's page
                  above: a judge stands at one problem, a manager walks one
                  person's card. With no athlete named, the last one picked. */}
              <Route path="/competitions/:slug/rounds/:roundId/athletes/:participantId?" element={
                <ProtectedRoute>
                  <TeamManagerAthlete />
                </ProtectedRoute>
              } />
              <Route path="/competitions/:slug/settings" element={
                <ProtectedRoute>
                  <CompetitionSettings />
                </ProtectedRoute>
              } />
              <Route path="/competitions/:gymSlug/climbs/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/competitions/:gymSlug/climbs/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/boulders/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/competitions/:gymSlug/boulders/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/routes/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/competitions/:gymSlug/routes/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/climbs/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/boulders/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/routes/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/climbs/:climbSlug" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/boulders/:climbSlug" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/routes/:climbSlug" element={<ClimbProfile />} />
              <Route path="/competitions/:gymSlug/boulders/new" element={<ClimbEditorPage />} />
              <Route path="/competitions/:gymSlug/routes/new" element={<ClimbEditorPage />} />
              <Route path="/gyms/:gymSlug/climbs/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/gyms/:gymSlug/climbs/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/boulders/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/gyms/:gymSlug/boulders/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/routes/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/gyms/:gymSlug/routes/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/climbs/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/boulders/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/routes/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/climbs/:climbSlug" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/boulders/:climbSlug/edit" element={<ClimbEditorPage />} />
              <Route path="/gyms/:gymSlug/routes/:climbSlug/edit" element={<ClimbEditorPage />} />
              <Route path="/gyms/:gymSlug/boulders/:climbSlug" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/routes/:climbSlug" element={<ClimbProfile />} />
              {/* Board climbs live under their board */}
              <Route path="/gyms/:gymSlug/boards/:boardId/climbs/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/gyms/:gymSlug/boards/:boardId/climbs/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/boards/:boardId/climbs/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/boards/:boardId/climbs/:climbSlug" element={<ClimbProfile />} />
              <Route path="/gyms/:gymSlug/boulders/new" element={<ClimbEditorPage />} />
              <Route path="/gyms/:gymSlug/routes/new" element={<ClimbEditorPage />} />
              <Route path="/gyms/:gymSlug/boards/:boardId/climbs/new" element={<ClimbEditorPage />} />
              <Route path="/gyms/:gymSlug/boards/:boardId/climbs/:climbSlug/edit" element={<ClimbEditorPage />} />
              <Route path="/crags/:slug" element={<CragProfile />} />
              <Route path="/crags/:slug/qr-poster" element={
                <ProtectedRoute>
                  <QrPosterPage kind="crag" />
                </ProtectedRoute>
              } />
              {/* The crag's routes on A4, made in the browser; for its admins and editors. */}
              <Route path="/crags/:slug/sheet" element={
                <ProtectedRoute>
                  <CragSheetPage />
                </ProtectedRoute>
              } />
              <Route path="/crags/:gymSlug/boulders/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/crags/:gymSlug/boulders/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/crags/:gymSlug/routes/:climbSlug/share/advance/new" element={<ShareClimbAdvancedPage />} />
              <Route path="/crags/:gymSlug/routes/:climbSlug/share" element={<ClimbProfile />} />
              <Route path="/crags/:gymSlug/boulders/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/crags/:gymSlug/routes/:climbSlug/video/:videoId" element={<ClimbProfile />} />
              <Route path="/crags/:gymSlug/boulders/:climbSlug" element={<ClimbProfile />} />
              <Route path="/crags/:gymSlug/routes/:climbSlug" element={<ClimbProfile />} />
              <Route path="/crags/:gymSlug/boulders/new" element={<ClimbEditorPage />} />
              <Route path="/crags/:gymSlug/routes/new" element={<ClimbEditorPage />} />
              {/* Whoever looks after a crag — reached from the crag's info tab. */}
              <Route path="/maintainers/:slug" element={<MaintainerProfile />} />
              <Route path="/contact" element={<ContactPage />} />
              {/* Public and unauthenticated: the banner links here before the
                  visitor has agreed to anything, let alone signed in. */}
              <Route path="/privacy-policy" element={<LegalPage document="privacy-policy" />} />
              <Route path="/cookie-policy" element={<LegalPage document="cookie-policy" />} />
              <Route path="/credits" element={<LegalPage document="credits" />} />
              {/* The children whose accounts this person holds — reached from
                  the settings page, since a managed account has no profile of
                  its own to visit. */}
              <Route
                path="/profile/athletes"
                element={
                  <ProtectedRoute>
                    <MyAthletesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notification-settings"
                element={
                  <ProtectedRoute>
                    <NotificationSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-integration"
                element={
                  <ProtectedRoute>
                    <AiIntegrationPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </main>
          <AppFooter />
          <BottomNav />
        </div>
        <Toaster />
        <ConsentBanner />
        <PWAUpdatePrompt />
        <ServiceWorkerStatus />
        <WorkoutTimerDialog />
        <EndWorkoutModal />
        <AnalyticsGuard />
        </AppFooterProvider>
        </BottomNavProvider>
        </BreadcrumbProvider>
        </NotificationProvider>
      </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  )
}

export default App
